import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getReportById,
  getReportList,
  ReportDetailsData,
  ReportItem,
} from "../../apis/weeklyReportApis";

interface ReportsState {
  reports: ReportItem[];
  selectedReport: ReportDetailsData | null;
  loading: boolean;
  detailsLoading: boolean;
  error: string | null;
}

const initialState: ReportsState = {
  reports: [],
  selectedReport: null,
  loading: false,
  detailsLoading: false,
  error: null,
};

/* =======================
   FETCH LIST
======================= */

export const fetchReports = createAsyncThunk<
  ReportItem[],
  void,
  { rejectValue: string }
>("reports/fetchReports", async (_, { rejectWithValue }) => {
  try {
    const response = await getReportList();

    if (!response.status) {
      return rejectWithValue("Failed to fetch reports");
    }

    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Something went wrong",
    );
  }
});

/* =======================
   FETCH DETAILS
======================= */

export const fetchReportById = createAsyncThunk<
  ReportDetailsData,
  string,
  { rejectValue: string }
>("reports/fetchReportById", async (id, { rejectWithValue }) => {
  try {
    const response = await getReportById(id);

    if (!response.status) {
      return rejectWithValue("Failed to fetch report details");
    }

    return response.data; // ✅ return full object
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.data ||
        error.response?.data?.message ||
        "Something went wrong",
    );
  }
});

/* =======================
   SLICE
======================= */

const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReports.fulfilled, (state, action) => {
        state.loading = false;
        state.reports = action.payload;
      })
      .addCase(fetchReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch reports";
      });

    builder
      .addCase(fetchReportById.pending, (state) => {
        state.detailsLoading = true;
        state.error = null;
        state.selectedReport = null;
      })
      .addCase(fetchReportById.fulfilled, (state, action) => {
        state.detailsLoading = false;
        state.selectedReport = action.payload;
      })
      .addCase(fetchReportById.rejected, (state, action) => {
        state.detailsLoading = false;
        state.error = action.payload || "Failed to fetch report details";
        state.selectedReport = null;
      });
  },
});

export default reportsSlice.reducer;
